import { forwardRef, useState, useRef, useImperativeHandle, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { useToast } from '@shared/hooks/use-toast';
import { useUpdateMapping, useIncidentTypes, useCouriers, useCourierIncidentKinds } from '../../hooks';
import { IncidentMapping } from '../../models/Mapping';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

export interface EditMappingModalRef extends ModalCenterRef {
  openWithMapping: (mapping: IncidentMapping) => void;
}

export const EditMappingModal = forwardRef<EditMappingModalRef>((_, ref) => {
  const { t } = useTranslation('mappingsModule');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: incidentTypes = [], isLoading: isLoadingTypes } = useIncidentTypes();
  const { data: couriersData = [] } = useCouriers();
  const updateMutation = useUpdateMapping();

  // Adaptar los couriers al formato necesario para el Select
  const couriers = useMemo(() => {
    return couriersData.map((courier: { id: number; name: string }) => ({
      label: courier.name,
      value: courier.id.toString(),
    }));
  }, [couriersData]);

  const internalModalRef = useRef<ModalCenterRef>(null);
  const [mapping, setMapping] = useState<IncidentMapping | null>(null);
  const [courierId, setCourierId] = useState<string>('');
  const [courierKindId, setCourierKindId] = useState<string>('');
  const [incidentTypeId, setIncidentTypeId] = useState<string | undefined>(undefined);

  // Cargar tipos del courier cuando cambia el courier (solo si no es "none")
  const { data: currentCourierKinds = [], isLoading: isLoadingCurrentCourierKinds } = useCourierIncidentKinds(
    courierId && courierId !== 'none' ? parseInt(courierId, 10) : null,
  );

  // 🎯 Actualizar campos cuando cambie el mapping
  useEffect(() => {
    if (mapping) {
      setCourierId(mapping.courier_id?.toString() || '');
      setCourierKindId(mapping.courier_incident_kind_id?.toString() || '');
      setIncidentTypeId(mapping.incident_type_id?.toString() || undefined);
    }
  }, [mapping]);

  const resetForm = () => {
    setMapping(null);
    setCourierId('');
    setCourierKindId('');
    setIncidentTypeId(undefined);
  };

  const handleCancel = () => {
    internalModalRef.current?.closeModal();
    resetForm();
  };

  const handleUpdate = async () => {
    if (!mapping) {
      toast({
        title: t('form.error.title'),
        description: t('form.error.unexpected'),
        variant: 'destructive',
      });
      return;
    }

    // Validar que si hay courier seleccionado, el tipo externo es obligatorio
    if (courierId && courierId !== 'none' && !courierKindId) {
      toast({
        title: t('form.error.title'),
        description: t('form.error.fill_required'),
        variant: 'destructive',
      });
      return;
    }

    // Si no hay courier o es "none", no se puede actualizar (tipo externo es obligatorio)
    if (!courierId || courierId === 'none') {
      toast({
        title: t('form.error.title'),
        description: t('form.error.select_courier'),
        variant: 'destructive',
      });
      return;
    }

    // Validar que el tipo interno es obligatorio en edición
    if (!incidentTypeId) {
      toast({
        title: t('form.error.title'),
        description: t('form.error.select_internal_type', 'Debes seleccionar un tipo interno'),
        variant: 'destructive',
      });
      return;
    }

    try {
      const response = await updateMutation.mutateAsync({
        id: mapping.id!,
        payload: {
          courier_incident_kind_id: parseInt(courierKindId, 10),
          incident_type_id: parseInt(incidentTypeId, 10),
        },
      });

      if (response.success) {
        toast({
          title: t('form.success.title'),
          description: response.message || t('form.success.update'),
          variant: 'default',
        });
        // 🎯 Invalidar queries para refrescar la lista
        queryClient.invalidateQueries({ queryKey: ['mappings'] });
        resetForm();
        handleCancel();
      } else {
        toast({
          title: t('form.error.title'),
          description: response.message || t('form.error.unexpected'),
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.log('🔍 EditMappingModal - Error:', error);
      toast({
        title: t('form.error.title'),
        description: error?.message || t('form.error.unexpected'),
        variant: 'destructive',
      });
    }
  };

  // 🎯 Exponer métodos personalizados usando useImperativeHandle
  const openWithMapping = (mappingData: IncidentMapping) => {
    setMapping(mappingData);
    internalModalRef.current?.openModal();
  };

  useImperativeHandle(ref, () => ({
    openModal: () => internalModalRef.current?.openModal(),
    closeModal: () => internalModalRef.current?.closeModal(),
    openWithMapping, // ← Método nuevo para uso directo
  }));

  const isLoading = updateMutation.isPending;

  return (
    <ModalCenter ref={internalModalRef} title={t('form.edit_title')} type="default">
      <div className="space-y-4">
        {/* Courier - Requerido */}
        {mapping && (
          <div>
            <label className="text-sm font-medium text-title mb-2 block">{t('form.courier')} *</label>
            <Select
              value={courierId}
              onValueChange={(value) => {
                setCourierId(value);
                // Resetear courier kind cuando cambia el courier
                setCourierKindId('');
              }}
              disabled={isLoading}
            >
              <SelectTrigger data-testid="modal-courier-select">
                <SelectValue placeholder={t('form.select_courier')} />
              </SelectTrigger>
              <SelectContent className="max-w-[var(--radix-select-trigger-width)]">
                {couriers.length > 0 ? (
                  couriers.map((courier: { label: string; value: string }) => (
                    <SelectItem key={courier.value} value={courier.value} className="whitespace-normal">
                      <span className="whitespace-normal break-words">{courier.label}</span>
                    </SelectItem>
                  ))
                ) : (
                  <div className="px-2 py-1.5 text-sm text-complementary-text text-center">{t('form.no_results')}</div>
                )}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Tipo externo (courier incident kind) - Requerido, solo visible cuando hay courier seleccionado (no "none") */}
        {courierId && courierId !== 'none' && (
          <div>
            <label className="text-sm font-medium text-title mb-2 block">{t('form.external_type')} *</label>
            <Select
              value={courierKindId}
              onValueChange={setCourierKindId}
              disabled={isLoadingCurrentCourierKinds || !courierId || courierId === 'none' || isLoading}
            >
              <SelectTrigger data-testid="modal-external-type-select">
                <SelectValue placeholder={t('form.select_external_type')} />
              </SelectTrigger>
              <SelectContent className="max-w-[var(--radix-select-trigger-width)]">
                {currentCourierKinds.length > 0
                  ? currentCourierKinds.map((kind) => (
                      <SelectItem key={kind.id} value={kind.id.toString()} className="whitespace-normal">
                        <span className="whitespace-normal break-words">{kind.external_label}</span>
                      </SelectItem>
                    ))
                  : !isLoadingCurrentCourierKinds && (
                      <div className="px-2 py-1.5 text-sm text-complementary-text text-center">
                        {t('form.no_results')}
                      </div>
                    )}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Tipo interno (editable) - Requerido en edición */}
        <div>
          <label className="text-sm font-medium text-title mb-2 block">{t('form.internal_type')} *</label>
          <Select value={incidentTypeId} onValueChange={setIncidentTypeId} disabled={isLoadingTypes || isLoading}>
            <SelectTrigger data-testid="modal-internal-type-select">
              <SelectValue placeholder={t('form.select_internal_type')} />
            </SelectTrigger>
            <SelectContent className="max-w-[var(--radix-select-trigger-width)]">
              {incidentTypes.length > 0
                ? incidentTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id.toString()} className="whitespace-normal">
                      <span className="whitespace-normal break-words">{type.label}</span>
                    </SelectItem>
                  ))
                : !isLoadingTypes && (
                    <div className="px-2 py-1.5 text-sm text-complementary-text text-center">
                      {t('form.no_results')}
                    </div>
                  )}
            </SelectContent>
          </Select>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 mt-6 pt-4">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
            data-testid="modal-cancel-button"
            size="lg"
          >
            {t('form.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdate}
            disabled={isLoading || !courierId || courierId === 'none' || !courierKindId || !incidentTypeId}
            data-testid="modal-submit-button"
            size="lg"
          >
            {isLoading ? t('form.saving') : t('form.update')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

EditMappingModal.displayName = 'EditMappingModal';
